// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

/**
 * Module Configuration context — Sprint 15.6.0.
 *
 * Fetches the org's module-config from the API and exposes
 * `isModuleEnabled(moduleId)` so the sidebar and route guards
 * can hide / block disabled modules.
 */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext.js';
import { apiClient } from '../services/api-client.js';

interface ModuleConfigContextValue {
  /** Returns true if the module is enabled (or config hasn't loaded yet). */
  isModuleEnabled: (moduleId: string) => boolean;
  /** Re-fetch config from API — call after saving changes. */
  refreshConfig: () => Promise<void>;
  loading: boolean;
  /**
   * Whether `isModuleEnabled` is answering from a read. It returns `true` both
   * before the config loads and after the read fails, which is right for the
   * sidebar and the route guards (a flash of hidden navigation, or an app
   * locked shut by one failed read, is worse). It is wrong for a page that
   * STATES enablement to the operator: such a page reads this and says it
   * could not check, rather than asserting an answer nobody read (Sprint
   * 63.11.0, gate E12.5 pass 1).
   */
  configState: 'loading' | 'answered' | 'failed';
}

const ModuleConfigContext = createContext<ModuleConfigContextValue | null>(null);

export function ModuleConfigProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [modules, setModules] = useState<Record<string, { enabled: boolean }>>({});
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [configState, setConfigState] = useState<'loading' | 'answered' | 'failed'>('loading');

  const fetchConfig = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setConfigState('loading');
    try {
      const result = await apiClient.getModuleConfig();
      if (result?.modules) {
        setModules(result.modules);
        setConfigState('answered');
      } else {
        setConfigState('failed');
      }
      setLoaded(true);
    } catch (err) {
      console.error('Failed to load module config:', err);
      // On error, default to all enabled so the app remains usable
      setLoaded(true);
      setConfigState('failed');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const value = useMemo<ModuleConfigContextValue>(
    () => ({
      isModuleEnabled: (moduleId: string) => {
        // Before config loads, treat all modules as enabled to avoid flash
        if (!loaded) return true;
        // If module not in config, default to enabled
        const toggle = modules[moduleId];
        return toggle?.enabled ?? true;
      },
      refreshConfig: fetchConfig,
      loading,
      configState,
    }),
    [modules, loaded, loading, configState, fetchConfig],
  );

  return <ModuleConfigContext.Provider value={value}>{children}</ModuleConfigContext.Provider>;
}

export function useModuleConfig(): ModuleConfigContextValue {
  const ctx = useContext(ModuleConfigContext);
  if (!ctx) throw new Error('useModuleConfig must be used within ModuleConfigProvider');
  return ctx;
}
